import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useCart } from "../lib/cart";
import { WhatsAppShareButton } from "../components/WhatsAppWidget";
import { MapPin, Truck, Star, SealCheck, Clock, Package } from "@phosphor-icons/react";
import { formatGBP } from "../lib/format";
import { toast } from "sonner";

export default function VendorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { postcode } = useCart();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: v } = await api.get(`/vendors/by-slug/${slug}`);
        setVendor(v);
        const { data: p } = await api.get(`/products?vendor_id=${v.vendor_id}`);
        setProducts(p);
        const { data: r } = await api.get(`/ratings/vendor/${v.vendor_id}`);
        setRatings(r);
        if (postcode) {
          const { data: d } = await api.get(`/vendors/check-delivery?vendor_id=${v.vendor_id}&postcode=${encodeURIComponent(postcode)}`);
          setDelivery(d);
        }
      } catch (e) {
        toast.error("Vendor not found");
        navigate("/vendors");
      }
      setLoading(false);
    })();
  }, [slug, postcode, navigate]);

  if (loading || !vendor) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[#5C5C54]">Loading...</div>;
  }

  return (
    <div className="pt-20 pb-24" data-testid="vendor-detail-page">
      {/* COVER */}
      <div className="relative h-48 md:h-72 overflow-hidden">
        <img src={vendor.cover_image_url} alt={vendor.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F9F7F3]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 -mt-10 md:-mt-16 relative">
        <div className="card-surface p-6 md:p-8" data-testid="vendor-header-card">
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-4 border-white bg-[#F2EFE9] flex-shrink-0 -mt-14 md:-mt-20 shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
              <img src={vendor.logo_url || vendor.cover_image_url} alt={vendor.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {vendor.badges?.includes("Verified Vendor") && (
                  <span className="chip-success"><SealCheck size={12} weight="fill" /> Verified</span>
                )}
                {vendor.badges?.includes("Top Rated") && (
                  <span className="chip-warning"><Star size={12} weight="fill" /> Top rated</span>
                )}
                {vendor.badges?.includes("Fast Delivery") && (
                  <span className="chip"><Truck size={12} weight="fill" /> Fast delivery</span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-black tracking-tighter mb-2">{vendor.name}</h1>
              <p className="text-sm md:text-base text-[#5C5C54] mb-4 max-w-2xl">{vendor.description}</p>

        <div className="flex items-center gap-3 text-xs mb-3">
                {vendor.ratings_count >= 5 && (
                  <span className="flex items-center gap-1.5 font-semibold">
                    <Star size={14} weight="fill" className="text-[#E4A951]" />
                    {vendor.ratings_avg?.toFixed(1)} · {vendor.ratings_count} reviews
                  </span>
                )}
                <span className="flex items-center gap-1.5 text-[#5C5C54]">
                  <Package size={14} weight="duotone" /> {vendor.total_orders || 0} orders completed
                </span>
                <span className="flex items-center gap-1.5 text-[#5C5C54]">
                  <Clock size={14} weight="duotone" /> Serving since {new Date(vendor.created_at).getFullYear()}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-[#5C5C54]">
                  <MapPin size={14} weight="duotone" /> Based in {vendor.delivery_config?.base_postcode}
                </span>
                <span className="flex items-center gap-1.5 text-[#5C5C54]">
                  <Truck size={14} weight="duotone" />
                  {vendor.delivery_config?.nationwide_shipping_enabled ? "UK-wide shipping" : "Local delivery only"}
                </span>
                <span className="flex items-center gap-1.5 text-[#5C5C54]">
                  <Clock size={14} weight="duotone" /> ~{vendor.delivery_config?.estimated_delivery_days} days
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-5">
                {vendor.contact?.whatsapp && (
                  <WhatsAppShareButton
                    phone={vendor.contact.whatsapp}
                    message={`Hi ${vendor.name}, I'm interested in your products on AfriPantry.`}
                    label="Contact on WhatsApp"
                    testid="vendor-whatsapp-btn"
                  />
                )}
              </div>
            </div>

            {/* Delivery panel */}
            <div className="md:w-72 flex-shrink-0 bg-[#F2EFE9] rounded-2xl p-4 border border-[#E5E2D9]" data-testid="delivery-panel">
              <p className="text-[10px] font-bold tracking-wider uppercase text-[#5C5C54] mb-2">Delivery to your postcode</p>
              {delivery ? (
                delivery.delivers ? (
                  <>
                    <p className="text-sm font-bold text-[#2B5329] mb-1.5">✓ Delivers to {postcode}</p>
                    <div className="text-xs text-[#5C5C54] space-y-1">
                      <p>Fee: <span className="font-bold text-[#1A1A18]">{formatGBP(delivery.fee)}</span> ({delivery.method})</p>
                      <p>Min. order: <span className="font-bold text-[#1A1A18]">{formatGBP(delivery.minimum_order)}</span></p>
                      <p>~{delivery.estimated_delivery_days} days</p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm font-bold text-[#A94438]">Does not deliver to {postcode}</p>
                )
              ) : (
                <p className="text-xs text-[#5C5C54]">Set your postcode at the top to check delivery.</p>
              )}
            </div>
          </div>
        </div>

        {/* PRODUCTS */}
        <section className="mt-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
              <Package size={24} weight="duotone" className="inline mr-2" />
              All products ({products.length})
            </h2>
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-[#5C5C54]">No active products.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </section>

        {/* REVIEWS */}
        {ratings.length > 0 && (
          <section className="mt-12" data-testid="vendor-reviews">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-6">Recent reviews</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ratings.slice(0, 6).map((r) => (
                <div key={r.rating_id} className="card-surface p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={14} weight={s <= r.stars ? "fill" : "regular"} className={s <= r.stars ? "text-[#E4A951]" : "text-[#E5E2D9]"} />
                    ))}
                  </div>
                  <p className="text-sm text-[#1A1A18] mb-2">{r.comment || "Great experience"}</p>
                  <p className="text-xs text-[#5C5C54] font-semibold">{r.user_name}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
